import { useMediaQuery } from '@mantine/hooks';

import type { TabsProps } from '@mantine/core';

/** A phone, as far as the detail views are concerned: Mantine's `sm` breakpoint. The overlay itself
 * goes full screen earlier (see DetailOverlay's FULLSCREEN_BELOW) - a tablet gets the full-screen
 * layer but still has the room for the desktop arrangement inside it. */
export const MOBILE_QUERY = '(max-width: 48em)';

/**
 * Whether to lay a view out for a phone. Evaluated on the FIRST render (not after an effect), so a
 * phone never flashes the desktop arrangement before it snaps to the mobile one.
 */
export const useIsMobile = (): boolean =>
    useMediaQuery(MOBILE_QUERY, false, { getInitialValueInEffect: false }) ?? false;

/**
 * Tabs that scroll sideways instead of squeezing their labels.
 *
 * Mantine's tab list shrinks every tab to fit, and the app's theme clips the label with an ellipsis
 * once it no longer does - on a phone that read "Attri…", "Verwendu…", "Dokument…", i.e. nothing
 * anyone could tell apart. Here no tab shrinks and the label is never clipped; whatever does not
 * fit scrolls. A list that fits (every desktop window) looks exactly as before.
 *
 * `styles` rather than classes, because Mantine applies them as inline styles - which is what
 * reliably beats @uag/doka-design's own rules (the reason the rest of the theme does the same).
 */
export const SCROLLABLE_TABS_STYLES: TabsProps['styles'] = {
    list: { flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' },
    tab: { flexShrink: 0, whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip', maxWidth: 'none' },
    tabLabel: { whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip', maxWidth: 'none' },
};

/**
 * The top heading of a view (`<Title order={1}>`) on a phone. The theme sets the h1 scale to 25px,
 * but on a phone the heading came out at roughly twice that and read as a poster ("SuperCurve"
 * filling the width of the system card). Set INLINE, because that is what reliably beats the
 * stylesheet rule that wins over the theme's scale; 22px keeps it the largest thing on the screen
 * without letting one word take a whole line. The desktop keeps the theme's size.
 */
export const MOBILE_H1_STYLE = { fontSize: '1.375rem', lineHeight: 1.2, overflowWrap: 'anywhere' } as const;
